import { createBlobUrl, base64toBlob, blobToBase64 } from '../utils/static-functions';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FileMapEntry, SignatureFileEntry } from 'common';
import { SharedApiService } from './shared-api.service';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { AuthService } from './auth.service';


@Injectable({ providedIn: 'root' })
export class FileService {

    private authService = inject(AuthService);
    private sanitizer = inject(DomSanitizer);
    private apiService = inject(SharedApiService);

    /**
     * Load files from an event into a local array.
     *
     * @param {*} files The files to load.
     * @param {Array<FileMapEntry>} fileArray The existing file array to add to.
     * @param {(file: any) => string} [filenameFn=(file: any) => file.name] The function to get the filename from, this can be overridden to get a different filename and defaults to `file.name`.
     * @return {Promise<Array<FileMapEntry>>} The array of files.
     */
    public async loadFilesFromEventToLocalArray(
        files: any,
        fileArray: Array<FileMapEntry>,
        filenameFn: (file: any) => string = (file: any) => file.name
    ): Promise<Array<FileMapEntry>> {
        if (!files || files.length < 1) return Promise.reject('No files provided.');

        const promises: Promise<void>[] = [];
        for (const file of files) {

            promises.push(new Promise<void>((innerResole, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    if (reader.result === null) {
                        reject('File reader result is null.');
                        return;
                    }

                    // Save file as blob to display in the UI.
                    const fileDatum: FileMapEntry = {
                        blob: base64toBlob(reader.result as string),
                        filename: filenameFn(file),
                        // id: new Date().getTime(),
                        id: null as unknown as number,
                        newFilename: `${new Date().getTime()}-${filenameFn(file)}`,
                        safeLocalUrl: '' as unknown as SafeUrl,
                        type: file.type,
                        localUrl: '',
                        isUploadedToServer: false
                    };
                    if (fileDatum.blob) fileDatum.localUrl = createBlobUrl(fileDatum.blob);
                    if (fileDatum.localUrl) fileDatum.safeLocalUrl = this.sanitizer.bypassSecurityTrustUrl(fileDatum.localUrl);

                    fileArray = fileArray || []; // Ensure fileArray is defined.
                    fileArray.push(fileDatum);
                    innerResole();
                };

                // Reject fileReader promise if there is an error.
                reader.onerror = (error) => {
                    console.error('Error reading file:', error);
                    reject(error);
                };
            }));
        }

        await Promise.all(promises);

        return fileArray;
    }

    public async uploadFileToServer(file: FileMapEntry): Promise<FileMapEntry> {
        file.isUploading = true;

        try {
            if (!file.blob) throw new Error('No file blob to upload.');
            const base64 = await blobToBase64(file.blob);
            // strip data:image/png;base64 from the base64 string
            const base64Parts = base64.split(',');
            const trimmedBase64 = base64Parts[1];

            const response = await firstValueFrom(this.apiService.uploadFile({
                content: trimmedBase64,
                contentType: file.type,
                fileName: file.newFilename || file.filename

            }));
            // file.serverUrl = response.url; // TODO swap back.
            file.serverUrl = file.safeLocalUrl as any; // TODO: remove when swapped.
            file.resource = response;
            file.isUploadedToServer = true;
            file.isUploading = false;
            file.isFailed = false;
            return file;
        } catch (error) {
            console.error('Error uploading file:', error);
            file.isUploading = false;
            file.isFailed = true;
            throw error;
        }
    }

    public async uploadSignatureToServer(entry: SignatureFileEntry, base64: string, filename: string): Promise<SignatureFileEntry> {
        entry.isUploading = true;
        try {
            // strip data:image/png;base64 from the base64 string
            const base64Parts = base64.split(',');
            const trimmedBase64 = base64Parts[1];

            const response = await firstValueFrom(this.apiService.uploadFile({
                content: trimmedBase64,
                contentType: 'image/png',
                fileName: filename
            }));

            entry.base64 = base64;
            entry.filename = filename;
            entry.resource = response;
            entry.isUploadedToServer = true;
            entry.isUploading = false;
            entry.isFailed = false;
            return entry;
        } catch (ex) {
            console.error('Error uploading signature:', ex);
            entry.isUploading = false;
            entry.isFailed = true;
            throw ex;
        }
    }


    /**
    * Different browsers have different ways of handling file URLs.
    * Chrome will render img urls in html, but for PDF it will download it.
    * Firefox will render img and pdf urls in html.
    * Not sure about Edge, Opera, Safari.
    * 
    * @param {(string | undefined | null)} url The URL to download the file from.
    * @memberof FileService
    */
    public async convertServerUrlToLocalUrl(url: string | undefined | null, contentType: string): Promise<string | undefined> {
        if (!url) return undefined;

        return fetch(url)
            .then(res => res.blob())
            .then(async blob => {
                const newBlob = new Blob([blob], { type: contentType });
                return URL.createObjectURL(newBlob);
            });

    }

}